import Color from "color";
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';

import IcoPlayCircle from "@floji-internal/mui-icons/PlayCircle";
import IcoStopCircle from "@floji-internal/mui-icons/StopCircle";

import { Strings } from "--root/strings/en";
import { TelephoneNumber } from "--root/utils/TelephoneNumber";
import { Emoji } from "--components/emoji";
import { ListEntryAction, ListEntryIcon, ListEntryText } from '--floponents-web/atoms/ListEntry';
import { usePersistedState } from '--floponents-web/hooks/usePersistedState';
import { PopperSurface } from '--floponents-web/molecules/PopperSurface';
import { Tooltip } from "--components/tooltip";

import { useMaybeLoggedInUser } from "--client/common/hooks/useLoggedInUser";

import { useToneGenerator } from '../../utils/tones/useToneGenerator';
import { Picker } from './Picker';

interface StyledCCPickerButtonProps {
    active?: boolean;
    dense?: boolean;
}
const StyledCCPickerButton = styled.button.attrs({ type: "button" }) <StyledCCPickerButtonProps>`
    ${props => props.theme.typography.body1}

    display: flex;
    align-items: center;

    height: 100%;
    margin: ${props => (props.dense
        ? props.theme.spacing(0, 1, 0, -1)
        : props.theme.spacing(0, 2, 0, -2))};
    padding: ${props => (props.dense
        ? props.theme.spacing(0, 1)
        : props.theme.spacing(0, 2))};
    outline: none;
    border: none;
 
    cursor: pointer;

    border-right: 1px solid ${props => (props.active
        ? Color(props.theme.palette.primary.main).alpha(0.2).toString()
        : props.theme.palette.ui.border)};

    background: ${props => (props.active
        ? Color(props.theme.palette.primary.main).alpha(0.2).toString()
        : "none")};

    transition: all ${props => props.theme.transitions.long};

    :hover {
        background: ${props => (props.active
        ? Color(props.theme.palette.primary.main).alpha(0.2).toString()
        : props.theme.palette.ui.highlight)};
    }

    &>span {
        display: block;
        color: ${props => props.theme.palette.text.secondary};
        margin: ${props => props.theme.spacing(0, 0, 0, 1)};
    }
`;

const StyledTonePreviewButton = styled.button.attrs({ type: "button" })`
    background: none;
    border: none;
    padding: 0;
`;
interface TonePreviewProps {
    country: TelephoneNumber.tCountryCode;
    isPreviewing?: boolean;
    onPreviewStart?: (country: TelephoneNumber.tCountryCode) => void;
    onPreviewStop?: () => void;
}
const TonePreviewButton = (props: TonePreviewProps) => {
    const { onPreviewStart, onPreviewStop } = props;
    const theme = useTheme();

    const handlePreviewClick = useCallback((
        ev: React.MouseEvent<HTMLButtonElement>
    ) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (props.isPreviewing) {
            onPreviewStop?.();
        } else {
            onPreviewStart?.(props.country);
        }
    }, [onPreviewStart, onPreviewStop, props.country, props.isPreviewing]);

    return (
        <StyledTonePreviewButton onClick={handlePreviewClick}>
            {props.isPreviewing
                ? <IcoStopCircle size="20px" color={theme.palette.text.secondary} />
                : <IcoPlayCircle size="20px" color={theme.palette.text.secondary} />}
        </StyledTonePreviewButton>
    );
};

export interface CCListProps {
    filter?: true
    showRecents?: true
    showTonePreview?: true;
    onPick?: (code: TelephoneNumber.tCountryCode) => void,
}

export interface CCPickerProps extends CCListProps {
    open: boolean,
    triggerRef?: React.RefObject<HTMLElement>,
    onClose?: () => void,
}


export const CCList = (props: CCListProps) => {
    const { onPick } = props;

    const user = useMaybeLoggedInUser();

    const [recents, setRecents] =
        usePersistedState<Array<TelephoneNumber.tCountryCode> | undefined>(
            undefined,
            JSON.stringify([user, `RecentCountryPicks`])
        );

    /** Sorted countries by name */
    const countries = useMemo(() => {
        const collator = new Intl.Collator();
        return TelephoneNumber.countryCodes
            .sort((a, b) => collator.compare(a.n, b.n));
    }, []);

    // Country whose tone is being previewed
    const [previewedToneCountry, setPreviewedToneCountry] =
        useState<TelephoneNumber.tCountryCode>();

    const toneGenerator = useToneGenerator();
    useEffect(() => {
        if (!previewedToneCountry) toneGenerator.stop();
    }, [previewedToneCountry, toneGenerator]);

    const handleTonePreviewStart = useCallback((
        country: TelephoneNumber.tCountryCode
    ) => {
        setPreviewedToneCountry(country);
        toneGenerator.stop();
        setTimeout(() => {
            toneGenerator.startRingingTone(country.a.toLowerCase() as any);
        }, 0);
    }, [toneGenerator]);

    const handleTonePreviewStop = useCallback(() => {
        setPreviewedToneCountry(undefined);
    }, []);


    const handlePick = useCallback((country: TelephoneNumber.tCountryCode) => {
        if (previewedToneCountry) setPreviewedToneCountry(undefined);
        onPick?.(country);
    }, [onPick, previewedToneCountry]);

    const handleRecentsUpdated = useCallback((
        newRecents: TelephoneNumber.tCountryCode[]
    ) => {
        setRecents(newRecents);
    }, [setRecents]);

    /** Filters country items by various key values. */
    const filterCountry = useCallback((
        country: TelephoneNumber.tCountryCode,
        filterTerm: string
    ) => (
        `+${country.t}`.includes(filterTerm) ||
        country.t.includes(filterTerm) ||
        country.n.toLowerCase().includes(filterTerm.toLocaleLowerCase()) ||
        country.a.toLowerCase().includes(filterTerm.toLocaleLowerCase()) ||
        country.f.includes(filterTerm)
    ), []);

    const renderCountry = useCallback((country: TelephoneNumber.tCountryCode) => <>
        <ListEntryIcon dense>
            <Emoji emoji={country.f} />
        </ListEntryIcon>
        <ListEntryText
            dense
            primary={country.n}
            secondary={`+${country.t}`}
        />
        {props.showTonePreview && (
            <ListEntryAction>
                <TonePreviewButton
                    country={country}
                    isPreviewing={country === previewedToneCountry}
                    onPreviewStart={handleTonePreviewStart}
                    onPreviewStop={handleTonePreviewStop}
                />
            </ListEntryAction>
        )}
    </>, [
        handleTonePreviewStart, handleTonePreviewStop, previewedToneCountry,
        props.showTonePreview
    ]);

    return (
        <Picker
            items={countries}
            recents={recents}
            keyFor={country => country.a}
            filterFor={filterCountry}
            renderFor={renderCountry}
            onPick={handlePick}
            onRecentsUpdated={handleRecentsUpdated}
            showFilter={props.filter}
            showRecents={props.showRecents}
            wrapListEntries
        />
    );
};

export const CCPicker = (props: CCPickerProps) => {
    const { onClose } = props;

    const handlePopperClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    return (
        <PopperSurface
            width={400}
            menu
            triggerRef={props.triggerRef ?? null}
            open={props.open}
            onClose={handlePopperClose}
            placement="bottom-start"
        >
            <CCList {...props} onPick={v => {
                props.onPick?.(v);
                handlePopperClose();
            }} />
        </PopperSurface>
    );
};

export interface CCPickerButtonProps {
    value?: TelephoneNumber.tCountryCode
    filter?: true
    showRecents?: true
    showTonePreview?: true;
    onChange?: (value: TelephoneNumber.tCountryCode) => void,
}
export const CCPickerButton = (props: CCPickerButtonProps) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [isOpen, setOpen] = useState<boolean>(false);

    return <>
        <StyledCCPickerButton ref={triggerRef} onClick={() => setOpen(true)} active={isOpen}>
            {props.value
                ? (
                    <Tooltip tip={props.value.n}>
                        <Emoji emoji={props.value.f} loading="lazy" />
                    </Tooltip>
                )
                : <Emoji emoji="🏳️" loading="lazy" />}
            <span>
                {props.value
                    ? `+${props.value.t}`
                    : Strings.countryCodePicker.selectCountry}
            </span>
        </StyledCCPickerButton>
        <CCPicker
            open={isOpen}
            onClose={() => setOpen(false)}
            triggerRef={triggerRef}
            onPick={props.onChange}
            filter={props.filter}
            showRecents={props.showRecents}
            showTonePreview={props.showTonePreview}
        />
    </>;
};
